// src/pages/CampaignQuestionPage.jsx
import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams, useLocation } from "react-router-dom";
import {
  Form,
  Button,
  Progress,
  Spin,
  Checkbox,
  notification,
  Collapse,
} from "antd";
import {
  useCreateResponseMutation,
  useCreateUserResponseMutation,
  useGetCampaignByIdQuery,
  useGetFormDetailsQuery,
  useGetFormQuestionsQuery,
  useGeneratePresignedUrlMutation,
} from "../../store/api/apiSlice";
import {
  uploadToPresignedUrl,
  generateFileName,
} from "../../utils/presignedUploader";
import { SurveyHeader } from "../../components/survey/SurveyHeader";
import CustomVideoPlayer from "../../components/common/CustomVideoPlayer";
import DynamicInput from "../../components/survey/DynamicInput";
import { motion, AnimatePresence } from "framer-motion";
import { AnanymousIcon } from "../../assets/svgs/icons";
import { RightOutlined } from "@ant-design/icons";
import {
  findConditionalNextQuestion,
  getVisibleInputEntries,
  isInputConditionMet,
} from "../../utils/inputConditions";
import { isRadioListInputType, isRadioListRequiredEmpty } from "../../utils/radioList";
import { getQuestionTitleNumber } from "../../utils/questionNumber";
import {
  appendSurveyTrackingParam,
  persistSurveyResponseId,
  persistSurveyTrackingToken,
} from "../../utils/surveyTracking";
import { buildSurveyUserInfo } from "../../utils/surveyContact";

const CampaignQuestionPage = () => {
  const { formId, questionNumber, campaignId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [form] = Form.useForm();
  const watchedResponses = Form.useWatch("responses", form) || [];
  const location = useLocation();
  const isCampaignSubmission = location.pathname.includes(
    "/campaign-submissions/",
  );

  const [createResponse] = useCreateResponseMutation();
  const [createUserResponse] = useCreateUserResponseMutation();

  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isValidating, setIsValidating] = useState(true);
  const [isValidLink, setIsValidLink] = useState(false);
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [conditionalNavigationStack, setConditionalNavigationStack] = useState(
    [],
  );
  const [currentFormIndex, setCurrentFormIndex] = useState(0);
  const [direction, setDirection] = useState(
    localStorage.getItem("surveyAnimationDirection") || "vertical",
  );
  const [generatePresignedUrl] = useGeneratePresignedUrlMutation();

  const [responses, setResponses] = useState(
    JSON.parse(localStorage.getItem(`survey_${formId}_responses`) || "{}"),
  );
  const sortedQuestions = [...(questionsData?.data || [])].sort(
    (a, b) => a.number - b.number,
  );

  const questions = sortedQuestions || [];
  const currentQuestion = questions[currentQuestionIndex];
  const questionTitleNumber = getQuestionTitleNumber(
    currentQuestion,
    currentQuestionIndex,
  );
  const totalQuestions = questions.length;
  const { data: campaignData, isLoading: campaignLoading } =
    useGetCampaignByIdQuery(campaignId);
  const { data: formData, isLoading: formLoading } = useGetFormDetailsQuery(
    formId || campaignData?.data?.forms[currentFormIndex]?._id,
  );
  const { data: questionsData, isLoading: questionsLoading } =
    useGetFormQuestionsQuery(
      formId || campaignData?.data?.forms[currentFormIndex]?._id,
    );
  const activeFormId =
    formId || campaignData?.data?.forms[currentFormIndex]?._id;
  const organizationTermsRoute = `/organization-terms-and-conditions?formId=${encodeURIComponent(
    activeFormId || "",
  )}`;
  const organizationPrivacyRoute = `/organization-privacy-policy?formId=${encodeURIComponent(
    activeFormId || "",
  )}`;

  const handleAnonymousChange = (e) => {
    if (!formData?.data?.anonymous) {
      setIsAnonymous(e.target.checked);
    }
  };

  useEffect(() => {
    persistSurveyTrackingToken(formId, searchParams);
  }, [formId, searchParams]);

  const withTrackingParam = (payload) =>
    appendSurveyTrackingParam(payload, formId, searchParams);

  useEffect(() => {
    const validateLink = async () => {
      try {
        setIsValidating(true);

        if (formData?.data?.status === "active") {
          setIsValidLink(true);
        }
      } catch (error) {
        setIsValidLink(false);
      } finally {
        setIsValidating(false);
      }
    };

    if (formData) {
      validateLink();
    }
  }, [formData]);

  const handleDirectionChange = (checked) => {
    const newDirection = checked ? "vertical" : "horizontal";
    setDirection(newDirection);
    localStorage.setItem("surveyAnimationDirection", newDirection);
  };

  useEffect(() => {
    if (campaignData?.data?.forms) {
      if (!formId) {
        const firstForm = campaignData.data.forms[0];
        navigate(
          `/campaign-submissions/question/${campaignId}/${firstForm?._id}/0`,
        );
      } else {
        const currentFormIndex = campaignData.data.forms.findIndex(
          (f) => f._id === formId,
        );
        setCurrentFormIndex(currentFormIndex !== -1 ? currentFormIndex : 0);
      }
    }
  }, [campaignData, formId, campaignId]);

  const handleNextForm = () => {
    if (campaignData?.data?.forms) {
      const nextFormIndex = currentFormIndex + 1;

      if (nextFormIndex < campaignData.data.forms.length) {
        const nextForm = campaignData.data.forms[nextFormIndex];
        navigate(
          `/campaign-submissions/question/${campaignId}/${nextForm._id}/0`,
        );
      } else {
        navigate(`/campaign-submissions/thank-you/${campaignId}`);
      }
    }
  };

  /** When `showButton` is omitted from the API, treat as visible (legacy data). */
  const isQuestionNavButtonVisible = (question) =>
    question?.showButton !== false;

  const getCampaignQuestionButtonLabel = () => {
    const t = currentQuestion?.buttonType;
    if (t === "start") return "Start";
    if (t === "next") return "Next";
    if (t === "finish") return "Finish";
    return "Next";
  };

  const FormNavigationButtons = () => {
    if (!campaignData?.data?.forms || !currentQuestion) return null;

    const isLastQuestionInForm = currentQuestionIndex === totalQuestions - 1;
    const hasNextForm = currentFormIndex < campaignData.data.forms.length - 1;

    return (
      <div className="flex justify-center w-full mb-4">
        {isLastQuestionInForm ? (
          hasNextForm ? (
            <Button
              type="primary"
              onClick={handleNextForm}
              className="w-full sm:w-auto px-8 py-2 rounded-2xl bg-[#202835] hover:bg-[#2f3b4d]"
            >
              Next Form
            </Button>
          ) : (
            <Button
              type="primary"
              htmlType="submit"
              loading={isSubmitting}
              className="w-full sm:w-auto px-8 py-2 rounded-2xl bg-[#202835] hover:bg-[#2f3b4d]"
            >
              Complete Campaign
            </Button>
          )
        ) : (
          <>
            {isQuestionNavButtonVisible(currentQuestion) && (
              <div className="flex justify-center w-full">
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={isSubmitting}
                  className="w-full sm:w-auto flex items-center justify-center gap-2 px-8 py-2 rounded-2xl my-3 mb-4"
                >
                  {getCampaignQuestionButtonLabel()}
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  useEffect(() => {
    if (questions) {
      const newIndex = questions.findIndex(
        (q) => q.number?.toString() === questionNumber,
      );

      if (newIndex !== -1) {
        setCurrentQuestionIndex(newIndex);
      } else {
        setCurrentQuestionIndex(0);
      }
    }
  }, [questionNumber, questionsData]);

  useEffect(() => {
    form.resetFields();

    if (currentQuestion && responses[currentQuestion._id]) {
      try {
        const questionResponses = responses[currentQuestion._id];
        const formattedResponses = currentQuestion.response.map((_, index) => {
          const responseKey = Object.keys(questionResponses)[index];
          return questionResponses[responseKey]?.value || null;
        });

        if (currentQuestionIndex === 0) {
          const savedData = JSON.parse(
            localStorage.getItem(`survey_${formId}_responses`) || "{}",
          );
          const firstQuestionData = savedData[currentQuestion._id];

          if (firstQuestionData) {
            form.setFieldsValue({
              anonymous: firstQuestionData.anonymous || false,
              terms: false,
            });
          }
        }
      } catch (error) {
        console.warn("Error setting form values:", error);
        form.resetFields();
      }
    }
  }, [currentQuestion, responses, currentQuestionIndex, formId]);

  if (formLoading || questionsLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Spin size="large" />
      </div>
    );
  }

  if (formData?.data?.status !== "active") {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Form Not Available
          </h2>
          <p className="text-gray-500">
            This form is currently inactive. Please try again later.
          </p>
        </div>
      </div>
    );
  }

  if (isValidating) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Spin size="large" />
      </div>
    );
  }

  if (!isValidLink) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Form Not Available
          </h2>
          <p className="text-gray-500">
            This form is currently inactive or has been completed.
          </p>
        </div>
      </div>
    );
  }

  const CampaignProgress = () => {
    if (!campaignData?.data?.forms) return null;

    return (
      <div className="bg-gray-50 p-4 rounded-xl mb-4">
        <div className="flex justify-between items-center">
          <div>
            <h3 className="text-sm font-medium">{campaignData.data.title}</h3>
            <p className="text-xs text-gray-500">
              Form {currentFormIndex + 1} of {campaignData.data.forms.length}
            </p>
          </div>
          <Progress
            percent={
              ((currentFormIndex + 1) / campaignData.data.forms.length) * 100
            }
            size="small"
            className="w-32"
          />
        </div>
      </div>
    );
  };

  if (campaignLoading || formLoading || questionsLoading) {
    return <Spin />;
  }

  const handleSubmit = async (values) => {
    try {
      if (formData.data.anonymous) {
        values.anonymous = true;
      }

      if (!values.responses) {
        values.responses = [];
      }

      let hasEmptyRequiredFields = false;
      currentQuestion?.response?.forEach((input, index) => {
        if (!isInputConditionMet(input, values.responses)) {
          return;
        }

        const response = values.responses?.[index];

        if (input.require !== false) {
          if (
            !response ||
            (input.input_type === "checkbox" && response.length === 0) ||
            (input.input_type === "slider" && !response) ||
            (input.input_type === "radio" && !response) ||
            (input.input_type === "record-video" && !response) ||
            (input.input_type === "file" && !response) ||
            (input.input_type === "multiple-files" &&
              (!response ||
                (Array.isArray(response) && response.length === 0))) ||
            isRadioListRequiredEmpty(input, response)
          ) {
            form.setFields([
              {
                name: ["responses", index.toString()],
                errors: [
                  `Please ${
                    input.input_type === "record-video"
                      ? "record a video before proceeding"
                      : input.input_type === "file" ||
                          input.input_type === "multiple-files"
                        ? "upload a file"
                        : input.input_type === "slider"
                      ? "select a rating"
                      : input.input_type === "checkbox"
                      ? "select at least one option"
                      : input.input_type === "radio"
                      ? "select an option"
                      : isRadioListInputType(input.input_type)
                      ? "select an option for every row"
                      : "provide a response"
                  }`,
                ],
              },
            ]);

            hasEmptyRequiredFields = true;
          }
        }
      });

      if (hasEmptyRequiredFields) {
        return;
      }

      setIsSubmitting(true);

      let formattedResponses = {};
      let videoUploadPromises = [];

      currentQuestion?.response?.forEach((input, index) => {
        if (!isInputConditionMet(input, values.responses)) {
          return;
        }

        const response = values.responses?.[index];

        if (response) {
          const responseKey =
            input.input_title || `question_${currentQuestion._id}_${index}`;

          if (input.input_type === "record-video") {
            if (typeof response === "string" && response.startsWith("http")) {
              formattedResponses[responseKey] = {
                type: "record-video",
                value: response,
                inputId: input._id,
              };
            } else if (response instanceof Blob || response instanceof File) {
              formattedResponses[responseKey] = {
                type: "record-video",
                value: "uploading_" + Date.now(),
                inputId: input._id,
              };

              const uploadPromise = (async () => {
                try {
                  const contentType = response.type || "video/webm";
                  const fileName = generateFileName(contentType, "recording");
                  const presignedResponse = await generatePresignedUrl({
                    fileName,
                    contentType,
                    expiresIn: 600,
                  }).unwrap();

                  if (!presignedResponse?.url) {
                    throw new Error("Failed to get presigned URL");
                  }

                  await uploadToPresignedUrl(
                    presignedResponse.url,
                    response,
                    contentType,
                  );

                  const fileUrl = presignedResponse.fileUrl;

                  if (fileUrl) {
                    const updatePayload = withTrackingParam({
                      form: formId,
                      question: currentQuestion._id,
                      response: JSON.stringify({
                        responses: {
                          ...formattedResponses,
                          [responseKey]: {
                            type: "record-video",
                            value: fileUrl,
                            inputId: input._id,
                          },
                        },
                        questionId: currentQuestion._id,
                        step: currentQuestionIndex + 1,
                      }),
                      step: Number(currentQuestionIndex + 1),
                      total_questions: Number(totalQuestions),
                      user: localStorage.getItem("SurevyUserId"),
                    });

                    await createResponse(updatePayload);
                  }
                } catch (error) {
                  console.error("Background video upload failed:", error);
                }
              })();

              videoUploadPromises.push(uploadPromise);
            }
          } else if (input.input_type === "slider") {
            formattedResponses[responseKey] = {
              type: "slider",
              value: response,
              inputId: input._id,
            };
          } else if (input.input_type === "checkbox") {
            formattedResponses[responseKey] = {
              type: "checkbox",
              value: Array.isArray(response) ? response : [response],
              inputId: input._id,
            };
          } else if (input.input_type === "file") {
            formattedResponses[responseKey] = {
              type: "file",
              value: response instanceof Blob ? "uploading" : response,
              inputId: input._id,
            };
          } else if (input.input_type === "multiple-files") {
            const items = Array.isArray(response) ? response : [response];

            formattedResponses[responseKey] = {
              type: "multiple-files",
              value: items.map((item) =>
                item instanceof Blob ? "uploading" : item,
              ),
              inputId: input._id,
            };
          } else {
            formattedResponses[responseKey] = {
              type: input.input_type,
              value: response,
              inputId: input._id,
            };
          }
        }
      });

      const payload = {
        responses: formattedResponses,
        questionId: currentQuestion._id,
        step: currentQuestion.number,
      };

      if (currentQuestionIndex === 0) {
        const contact = buildSurveyUserInfo(currentQuestion, values);
        const userInfo = {
          full_name: contact.full_name || "",
          email: contact.email || "",
        };

        const createuser = await createUserResponse(userInfo).unwrap();
        localStorage.setItem("SurevyUserId", createuser.data._id);

        const createPayload = withTrackingParam({
          user: createuser.data._id,
          form: formId,
          question: currentQuestion._id,
          response: JSON.stringify({
            ...payload,
            anonymous: values.anonymous || false,
            terms: values.terms || false,
          }),
          step: Number(currentQuestion.number),
          total_questions: Number(totalQuestions),
        });

        const response = await createResponse(createPayload).unwrap();

        if (response?.data?._id) {
          persistSurveyResponseId(formId, response.data._id);
        }
      } else {
        const updatePayload = withTrackingParam({
          form: formId,
          question: currentQuestion._id,
          response: JSON.stringify(payload),
          step: Number(currentQuestion.number),
          total_questions: Number(totalQuestions),
          user: localStorage.getItem("SurevyUserId"),
        });

        await createResponse(updatePayload);
      }

      Promise.all(videoUploadPromises).catch((error) => {
        console.error("Some video uploads failed:", error);
      });

      const updatedResponses = {
        ...responses,
        [currentQuestion._id]: formattedResponses,
      };

      setResponses(updatedResponses);

      localStorage.setItem(
        `survey_${formId}_responses`,
        JSON.stringify(updatedResponses),
      );

      const conditionalNextQuestion = findConditionalNextQuestion(
        questions,
        currentQuestion,
        values.responses,
      );

      if (conditionalNextQuestion) {
        setConditionalNavigationStack((prev) => [
          ...prev,
          currentQuestionIndex,
        ]);
        navigate(
          `/campaign-submissions/question/${campaignId}/${formId}/${conditionalNextQuestion.number}`,
        );
      } else if (conditionalNavigationStack.length > 0) {
        const returnToIndex = conditionalNavigationStack[0];
        setConditionalNavigationStack([]);
        const nextQuestion = questions[returnToIndex + 1];
        navigate(
          `/campaign-submissions/question/${campaignId}/${formId}/${nextQuestion.number}`,
        );
      } else {
        if (currentQuestionIndex === totalQuestions - 1) {
          if (
            campaignData?.data?.forms &&
            currentFormIndex < campaignData.data.forms.length - 1
          ) {
            const nextForm = campaignData.data.forms[currentFormIndex + 1];
            navigate(
              `/campaign-submissions/question/${campaignId}/${nextForm._id}/0`,
            );
          } else {
            navigate(`/campaign-submissions/thank-you/${campaignId}`);
          }
        } else {
          const nextQuestion = questions[currentQuestionIndex + 1];
          navigate(
            `/campaign-submissions/question/${campaignId}/${formId}/${nextQuestion.number}`,
          );
        }
      }

      setIsSubmitting(false);
    } catch (error) {
      setIsSubmitting(false);

      notification.error({
        message: "Error",
        description: error.message || "Failed to save response",
      });
    }
  };

  const getAnimationProps = () => {
    if (direction === "vertical") {
      return {
        initial: { y: 40, opacity: 0 },
        animate: { y: 0, opacity: 1 },
        exit: { y: -40, opacity: 0 },
      };
    } else {
      return {
        initial: { x: 40, opacity: 0 },
        animate: { x: 0, opacity: 1 },
        exit: { x: -40, opacity: 0 },
      };
    }
  };

  {
    isCampaignSubmission && <CampaignProgress />;
  }

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={currentQuestion?._id || "loading"}
        {...getAnimationProps()}
        transition={{ duration: 0.3 }}
        className="p-1 sm:p-3 flex flex-col min-h-[100dvh]"
      >
        <div className="p-0 sm:p-3 flex flex-col flex-1 min-h-0">
          <div className="w-full mx-auto flex flex-col flex-1 min-h-0">
            <div className="hidden md:block shrink-0">
              <SurveyHeader
                onDirectionChange={handleDirectionChange}
                organization={formData?.data?.organization}
                form={formData?.data}
                onCancel={() => navigate("/")}
              />
              <div className="p-4 border rounded-2xl mb-4">
                <div className="w-full">
                  <div className="flex flex-wrap justify-between items-center m-0">
                    <h1 className="text-lg font-bold text-primary mt-0">
                      {formData?.data?.title}
                    </h1>
                    {isAnonymous && (
                      <div className="flex items-center gap-2 bg-gray-50 px-3 py-1 rounded-full">
                        <span className="text-sm">
                          <span className="relative top-1">
                            <AnanymousIcon />
                          </span>
                          Anonymous Response
                        </span>
                      </div>
                    )}
                  </div>
                  <p className="text-sm text-gray-500 m-0">
                    {formData?.data?.description}
                  </p>
                  <Progress
                    percent={
                      ((currentQuestionIndex + 1) / totalQuestions) * 100
                    }
                    showInfo={false}
                    strokeColor="#202835"
                    className="p-0"
                  />
                </div>
              </div>
            </div>
            <div className="md:hidden shrink-0">
              <Collapse
                ghost
                expandIconPosition="end"
                className={`bg-[${formData?.data?.organization?.color}] rounded-2xl mb-4`}
                expandIcon={({ isActive }) => (
                  <RightOutlined
                    rotate={isActive ? 90 : 0}
                    className="text-gray-500"
                  />
                )}
                items={[
                  {
                    key: "1",
                    label: (
                    <div className="flex items-center justify-between w-full">
                      <div className="flex items-center gap-2 ">
                        <img
                          src={formData?.data?.organization?.logo}
                          alt={formData?.data?.organization?.name}
                          className="w-8 h-8 rounded-lg object-cover"
                        />
                        <span className="font-medium">
                          {formData?.data?.organization?.name}
                        </span>
                      </div>
                      {isAnonymous && (
                        <div className="flex items-center gap-2 bg-gray-50 px-3 py-1 rounded-full">
                          <span className="text-sm">
                            <span className="relative top-1">
                              <AnanymousIcon />
                            </span>
                            Anonymous
                          </span>
                        </div>
                      )}
                    </div>
                    ),
                    children: (
                      <div className="space-y-4 pt-4">
                        <h1 className="text-lg font-bold text-primary">
                          {formData?.data?.title}
                        </h1>
                        <p className="text-sm text-gray-500 text-justify">
                          {formData?.data?.description}
                        </p>
                        <Progress
                          percent={
                            ((currentQuestionIndex + 1) / totalQuestions) * 100
                          }
                          showInfo={false}
                          strokeColor="#202835"
                          className="m-0"
                        />
                      </div>
                    ),
                  },
                ]}
              />
            </div>
            <Form
              form={form}
              onFinish={handleSubmit}
              initialValues={{
                ...responses[currentQuestion?._id],
                terms: false,
              }}
              layout="vertical"
              className="flex flex-col flex-1 min-h-0"
            >
              <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden">
                <div className="mx-auto box-border grid w-full min-w-0 max-w-full grid-cols-1 gap-4 rounded-3xl border bg-[#F5F5F5] p-1 md:grid-cols-[minmax(0,380px)_minmax(0,1fr)] md:p-3 items-start">
                  <div className="hidden min-w-0 max-w-full md:block md:justify-self-center">
                    {currentQuestion?.video && (
                      <CustomVideoPlayer
                        autoPlay
                        loop
                        preload="metadata"
                        src={currentQuestion.video}
                        playsInline
                      />
                    )}
                  </div>
                  <div className="min-w-0 max-w-full">
                    <div className="order-1 bg-white p-3 md:p-4 rounded-3xl border mb-2 md:mb-3 ">
                      <h2 className="text-xl sm:text-2xl lg:text-base font-bold mt-0 mb-0">
                        {questionTitleNumber != null
                          ? `${questionTitleNumber}) `
                          : ""}
                        {currentQuestion?.title}
                      </h2>
                      {currentQuestion?.description && (
                        <p className="text-gray-500 text-justify">
                          {currentQuestion?.description}
                        </p>
                      )}
                    </div>
                    <div className="bg-white p-1 md:p-4 rounded-3xl border mb-0">
                      <div className="mb-2 w-full min-w-0 max-w-full overflow-hidden md:hidden">
                        {currentQuestion?.video && (
                          <CustomVideoPlayer
                            autoPlay
                            loop
                            preload="metadata"
                            src={currentQuestion.video}
                            playsInline
                          />
                        )}
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-1 gap-0 ">
                        {getVisibleInputEntries(
                          currentQuestion?.response,
                          watchedResponses,
                        ).map(({ input, index }) => (
                          <DynamicInput
                            key={input._id || `${input.input_title}-${index}`}
                            input={input}
                            form={form}
                            index={index}
                          />
                        ))}
                      </div>
                      {currentQuestionIndex === 0 && (
                        <div className="flex">
                          <div>
                            {!formData?.data?.anonymous &&
                              currentQuestion?.showAnonymous && (
                                <Form.Item
                                  className="mb-0"
                                  name="anonymous"
                                  valuePropName="checked"
                                >
                                  <Checkbox onChange={handleAnonymousChange}>
                                    Make My Response{" "}
                                    <span className="">
                                      Anonymous
                                      <span className="relative top-1">
                                        <AnanymousIcon />
                                      </span>
                                    </span>
                                  </Checkbox>
                                </Form.Item>
                              )}
                            {currentQuestion?.showTerms && (
                              <Form.Item
                                className="mb-0"
                                name="terms"
                                valuePropName="checked"
                                rules={[
                                  {
                                    validator: async (_, value) => {
                                      if (!value) {
                                        throw new Error(
                                          "Please accept the terms & conditions",
                                        );
                                      }
                                    },
                                  },
                                ]}
                                required
                              >
                                <Checkbox>
                                  Accept our{" "}
                                  <a
                                    target="terms-and-conditions"
                                    href={organizationTermsRoute}
                                    className="underline cursor-pointer"
                                  >
                                    Terms & Conditions
                                  </a>{" "}
                                  and{" "}
                                  <a
                                    target="_blank"
                                    href={organizationPrivacyRoute}
                                    className="underline cursor-pointer"
                                  >
                                    Privacy Policy
                                  </a>
                                </Checkbox>
                              </Form.Item>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="shrink-0 flex justify-center py-3 px-2 border-t border-gray-100 bg-[#fafafa] md:bg-white">
                <FormNavigationButtons />
              </div>
            </Form>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default CampaignQuestionPage;
